import { RoutesService, eLayoutType } from '@abp/ng.core';
import { inject, provideAppInitializer } from '@angular/core';

export const APP_ROUTE_PROVIDER = [
  provideAppInitializer(() => {
    configureRoutes();
  }),
];

function configureRoutes() {
  const routes = inject(RoutesService);
  routes.add([
    // ── 1. Dashboard ──────────────────────────────────────────
    {
      path: '/dashboard',
      name: '::Menu:Dashboard',
      requiredPolicy: 'MallManagement.Dashboard',
      iconClass: 'fas fa-chart-line',
      order: 1,
      layout: eLayoutType.application,
    },

    // ── 2. Property Management ────────────────────────────────
    {
      name: '::Menu:PropertyManagement',
      iconClass: 'fas fa-building',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/malls',
      name: '::Menu:Malls',
      parentName: '::Menu:PropertyManagement',
      requiredPolicy: 'MallManagement.Malls',
      iconClass: 'fas fa-city',
      order: 1,
      layout: eLayoutType.application,
    },
    {
      path: '/shops',
      name: '::Menu:Shops',
      parentName: '::Menu:PropertyManagement',
      requiredPolicy: 'MallManagement.Shops',
      iconClass: 'fas fa-store-alt',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/tenants',
      name: '::Menu:Tenants',
      parentName: '::Menu:PropertyManagement',
      requiredPolicy: 'MallManagement.Tenants',
      iconClass: 'fas fa-users',
      order: 3,
      layout: eLayoutType.application,
    },
    {
      path: '/contracts',
      name: '::Menu:Contracts',
      parentName: '::Menu:PropertyManagement',
      requiredPolicy: 'MallManagement.Rentals',
      iconClass: 'fas fa-file-contract',
      order: 4,
      layout: eLayoutType.application,
    },

    // ── 3. Utilities ──────────────────────────────────────────
    {
      name: '::Menu:Utilities',
      iconClass: 'fas fa-plug',
      order: 3,
      layout: eLayoutType.application,
    },
    {
      path: '/electricity/meters',
      name: '::Menu:Electricity:Meters',
      parentName: '::Menu:Utilities',
      requiredPolicy: 'MallManagement.Utilities',
      iconClass: 'fas fa-gauge-high',
      order: 1,
      layout: eLayoutType.application,
    },
    {
      path: '/electricity/bills',
      name: '::Menu:Electricity:Bills',
      parentName: '::Menu:Utilities',
      requiredPolicy: 'MallManagement.Utilities',
      iconClass: 'fas fa-file-invoice',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/electricity/billing-units',
      name: '::Menu:Electricity:BillingUnits',
      parentName: '::Menu:Utilities',
      requiredPolicy: 'MallManagement.Utilities',
      iconClass: 'fas fa-ruler-combined',
      order: 3,
      layout: eLayoutType.application,
    },
    {
      path: '/electricity/supply-categories',
      name: '::Menu:Electricity:SupplyCategories',
      parentName: '::Menu:Utilities',
      requiredPolicy: 'MallManagement.UtilitySupplyCategories',
      iconClass: 'fas fa-layer-group',
      order: 4,
      layout: eLayoutType.application,
    },
    {
      path: '/fuel',
      name: '::Menu:Fuel',
      parentName: '::Menu:Utilities',
      requiredPolicy: 'MallManagement.Fuel',
      iconClass: 'fas fa-gas-pump',
      order: 5,
      layout: eLayoutType.application,
    },

    // ── 4. Services ───────────────────────────────────────────
    {
      name: '::Menu:Services',
      iconClass: 'fas fa-concierge-bell',
      order: 4,
      layout: eLayoutType.application,
    },
    {
      path: '/ancillary-services',
      name: '::Menu:AncillaryServices',
      parentName: '::Menu:Services',
      requiredPolicy: 'MallManagement.ServiceSubscriptions',
      iconClass: 'fas fa-hand-holding-heart',
      order: 1,
      layout: eLayoutType.application,
    },

    // ── 5. Finance ────────────────────────────────────────────
    {
      name: '::Menu:Finance',
      iconClass: 'fas fa-coins',
      order: 5,
      layout: eLayoutType.application,
    },
    {
      path: '/rent',
      name: '::Menu:Rent',
      parentName: '::Menu:Finance',
      requiredPolicy: 'MallManagement.RentPayments',
      iconClass: 'fas fa-cash-register',
      order: 1,
      layout: eLayoutType.application,
    },
    {
      path: '/contract-account',
      name: '::Menu:ContractAccounts',
      parentName: '::Menu:Finance',
      requiredPolicy: 'MallManagement.ContractAccounts',
      iconClass: 'fas fa-file-invoice-dollar',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/cash-remittances',
      name: '::Menu:CashRemittances',
      parentName: '::Menu:Finance',
      requiredPolicy: 'MallManagement.Remittances',
      iconClass: 'fas fa-hand-holding-usd',
      order: 3,
      layout: eLayoutType.application,
    },
    {
      path: '/billing-documents',
      name: '::Menu:BillingDocuments',
      parentName: '::Menu:Finance',
      requiredPolicy: 'MallManagement.FinancialDocuments.View',
      iconClass: 'fas fa-folder-open',
      order: 4,
      layout: eLayoutType.application,
    },
    {
      path: '/discount-policies',
      name: '::Menu:DiscountPolicies',
      parentName: '::Menu:Finance',
      requiredPolicy: 'MallManagement.DiscountPolicies',
      iconClass: 'fas fa-percentage',
      order: 5,
      layout: eLayoutType.application,
    },
    {
      path: '/fines',
      name: '::Menu:Fines',
      parentName: '::Menu:Finance',
      requiredPolicy: 'MallManagement.Fines',
      iconClass: 'fas fa-gavel',
      order: 6,
      layout: eLayoutType.application,
    },
    {
      path: '/fine-types',
      name: '::Menu:FineTypes',
      parentName: '::Menu:Finance',
      requiredPolicy: 'MallManagement.FineTypes',
      iconClass: 'fas fa-scale-balanced',
      order: 7,
      layout: eLayoutType.application,
    },

    // ── 6. Operations ─────────────────────────────────────────
    {
      name: '::Menu:Operations',
      iconClass: 'fas fa-tasks',
      order: 6,
      layout: eLayoutType.application,
    },
    {
      path: '/assignments',
      name: '::Menu:Assignments',
      parentName: '::Menu:Operations',
      requiredPolicy: 'MallManagement.Utilities',
      iconClass: 'fas fa-user-cog',
      order: 1,
      layout: eLayoutType.application,
    },

    // ── 7. Reports & Analytics ────────────────────────────────
    {
      name: '::Menu:ReportsAnalytics',
      iconClass: 'fas fa-chart-bar',
      order: 7,
      layout: eLayoutType.application,
    },
    {
      path: '/electricity/billing-report',
      name: '::Menu:Electricity:BillingReport',
      parentName: '::Menu:ReportsAnalytics',
      requiredPolicy: 'MallManagement.Reports.View',
      iconClass: 'fas fa-chart-pie',
      order: 1,
      layout: eLayoutType.application,
    },
    {
      path: '/tenant-meter-financial-report',
      name: '::Menu:TenantMeterFinancialReport',
      parentName: '::Menu:ReportsAnalytics',
      requiredPolicy: 'MallManagement.TenantMeterFinancialReport.View',
      iconClass: 'fas fa-file-lines',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/reports/consolidated-rent-statement',
      name: '::Menu:ConsolidatedRentStatement',
      parentName: '::Menu:ReportsAnalytics',
      requiredPolicy: 'MallManagement.Reports.View',
      iconClass: 'fas fa-file-invoice-dollar',
      order: 3,
      layout: eLayoutType.application,
    },
    {
      path: '/energy-flow',
      name: '::Menu:EnergyFlow',
      parentName: '::Menu:ReportsAnalytics',
      requiredPolicy: 'MallManagement.EnergyAnalytics.ViewEnergyFlow',
      iconClass: 'fas fa-arrows-spin',
      order: 4,
      layout: eLayoutType.application,
    },
    {
      path: '/energy-analytics',
      name: '::Menu:EnergyAnalytics',
      parentName: '::Menu:ReportsAnalytics',
      requiredPolicy: 'MallManagement.EnergyAnalytics',
      iconClass: 'fas fa-bolt',
      order: 4,
      layout: eLayoutType.application,
    },
    {
      path: '/data-quality',
      name: '::Menu:DataQuality',
      parentName: '::Menu:ReportsAnalytics',
      requiredPolicy: 'MallManagement.DataQuality',
      iconClass: 'fas fa-shield-halved',
      order: 5,
      layout: eLayoutType.application,
    },

    // ── 8. Configuration ──────────────────────────────────────
    {
      name: '::Menu:Configuration',
      iconClass: 'fas fa-sliders',
      order: 8,
      layout: eLayoutType.application,
    },
    {
      path: '/accounting-periods',
      name: '::Menu:AccountingPeriods',
      parentName: '::Menu:Configuration',
      requiredPolicy: 'MallManagement.Accounting',
      iconClass: 'fas fa-calendar-check',
      order: 1,
      layout: eLayoutType.application,
    },
    {
      path: '/financial-settings',
      name: '::Menu:FinancialSettings',
      parentName: '::Menu:Configuration',
      requiredPolicy: 'MallManagement.Admin.Global',
      iconClass: 'fas fa-cog',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/import-data',
      name: '::Menu:ImportData',
      parentName: '::Menu:Configuration',
      requiredPolicy: 'MallManagement.Admin.ImportData',
      iconClass: 'fas fa-file-excel',
      order: 3,
      layout: eLayoutType.application,
    },

    // ── 100. Administration (unchanged) ───────────────────────
    {
      name: 'AbpUiNavigation::Menu:Administration',
      iconClass: 'fas fa-wrench',
      order: 100,
      layout: eLayoutType.application,
    },
    {
      path: '/import-data',
      name: '::Menu:ImportData',
      parentName: 'AbpUiNavigation::Menu:Administration',
      requiredPolicy: 'MallManagement.Admin.ImportData',
      iconClass: 'fas fa-file-excel',
      order: 0,
      layout: eLayoutType.application,
    },
    {
      name: 'AbpIdentity::Menu:IdentityManagement',
      parentName: 'AbpUiNavigation::Menu:Administration',
      iconClass: 'fas fa-id-card',
      order: 1,
      layout: eLayoutType.application,
    },
    {
      path: '/identity/roles',
      name: 'AbpIdentity::Roles',
      parentName: 'AbpIdentity::Menu:IdentityManagement',
      requiredPolicy: 'AbpIdentity.Roles',
      iconClass: 'fas fa-user-shield',
      order: 1,
      layout: eLayoutType.application,
    },
    {
      path: '/identity/users',
      name: 'AbpIdentity::Users',
      parentName: 'AbpIdentity::Menu:IdentityManagement',
      requiredPolicy: 'AbpIdentity.Users',
      iconClass: 'fas fa-user-friends',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/tenant-management/tenants',
      name: 'AbpTenantManagement::Menu:TenantManagement',
      parentName: 'AbpUiNavigation::Menu:Administration',
      requiredPolicy: 'AbpTenantManagement.Tenants',
      iconClass: 'fas fa-users-cog',
      order: 2,
      layout: eLayoutType.application,
    },
    {
      path: '/setting-management',
      name: 'AbpSettingManagement::Settings',
      parentName: 'AbpUiNavigation::Menu:Administration',
      requiredPolicy: 'AbpSettingManagement.Settings',
      iconClass: 'fas fa-cog',
      order: 3,
      layout: eLayoutType.application,
    },
  ]);
}
